
import { RibaStage } from "@/types";
import { 
  CheckIcon, 
  ChevronRightIcon, 
  Activity, 
  AlertTriangle, 
  BarChart3, 
  CalendarIcon, 
  ClipboardList, 
  Clock,
  FileCheck,
  Ban,
  LucideIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

type RibaStagesProps = {
  stages: RibaStage[];
  currentStage: number;
  onClick?: (stageId: number) => void;
}

export default function RibaStages({ stages = [], currentStage, onClick }: RibaStagesProps) {
  const navigate = useNavigate();
  
  console.log('RibaStages - stages received:', stages);
  
  // Calculate the percentage of the current stage completed
  const currentProgress = stages[currentStage]?.progress || 0;
  
  const getStageStatusIcon = (stage: RibaStage) => {
    if (stage.status === "completed") {
      return <CheckIcon className="h-5 w-5 text-green-500" />;
    } else if (stage.status === "in-progress") {
      return <Activity className="h-5 w-5 text-blue-500" />;
    } else {
      return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStageColor = (stage: RibaStage) => {
    if (stage.status === "completed") {
      return "bg-green-500";
    } else if (stage.status === "in-progress") {
      return "bg-blue-500";
    } else {
      return "bg-gray-300";
    }
  };
  
  const handleStageClick = (stageId: number) => {
    if (onClick) {
      onClick(stageId);
    }
    navigate(`/stage/${stageId}`);
  };

  const getStageDetails = (stage: RibaStage) => {
    let details = "";
    if (stage.status === "completed") {
      details = "Completed";
      if (stage.dateCompleted) {
        details += ` on ${stage.dateCompleted.toLocaleDateString()}`;
      }
    } else if (stage.status === "in-progress") {
      details = `In Progress (${stage.progress}% complete)`;
      if (stage.dateStarted) {
        details += `\nStarted on ${stage.dateStarted.toLocaleDateString()}`;
      }
    } else {
      details = "Not Started";
    }
    
    // Add activities information
    const activitiesCount = stage.activities?.length || 0;
    details += `\n${activitiesCount} activities`;
    
    // Add approvals information
    const approvalsCount = stage.approvals?.length || 0;
    const pendingApprovals = stage.approvals?.filter(a => a.status === "pending").length || 0;
    
    if (approvalsCount > 0) {
      details += `\n${pendingApprovals} pending approvals`;
    }
    
    // Add information exchange details
    if (stage.informationExchanges && stage.informationExchanges.length > 0) {
      const completedExchanges = stage.informationExchanges.filter(
        ex => ex.status === "complete" || ex.status === "validated"
      ).length;
      
      details += `\n${completedExchanges}/${stage.informationExchanges.length} information exchanges`;
    }
    
    return details;
  };

  const getInfoExchangeStatus = (stage: RibaStage) => {
    if (!stage.informationExchanges || stage.informationExchanges.length === 0) {
      return { complete: 0, total: 0, percentage: 0 };
    }
    
    const total = stage.informationExchanges.length;
    const complete = stage.informationExchanges.filter(
      item => item.status === "complete" || item.status === "validated"
    ).length;
    
    return {
      complete,
      total,
      percentage: Math.round((complete / total) * 100)
    };
  };

  // Calculate overall project progress as percentage of completed stages
  const calculateOverallProgress = () => {
    const completedStages = stages.filter(s => s.status === "completed").length;
    const inProgressContribution = stages[currentStage].status === "in-progress" 
      ? stages[currentStage].progress / 100 / stages.length 
      : 0;
    
    return Math.round((completedStages / stages.length + inProgressContribution) * 100);
  };

  return (
    <div className="mb-8">
      <div className="mb-2">
        <h2 className="text-2xl font-semibold">RIBA Work Plan</h2>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {stages[currentStage]?.name} ({currentProgress}% Complete)
          </div>
          <div className="text-sm font-medium">
            Overall Progress: {calculateOverallProgress()}%
          </div>
        </div>
      </div>
      
      {/* Overall progress */}
      <div className="h-2 w-full bg-gray-100 rounded-full mb-6">
        <div 
          className="h-2 bg-blue-500 rounded-full"
          style={{ width: `${calculateOverallProgress()}%` }}
        />
      </div>

      {/* RIBA stages */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4 list-none mb-6">
        {stages.map((stage) => (
          <TooltipProvider key={stage.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card
                  className={`riba-stage cursor-pointer hover:shadow-md transition-all transform hover:translate-y-[-2px] ${
                    stage.id === currentStage ? "ring-2 ring-primary" : ""
                  } ${stage.status === "completed" ? "bg-green-50" : ""}`}
                  onClick={() => handleStageClick(stage.id)}
                >
                  <CardHeader className="p-3">
                    <div className="flex items-center justify-between">
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-white text-xs ${getStageColor(stage)}`}
                      >
                        {stage.id}
                      </div>
                      {getStageStatusIcon(stage)}
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <h3 className="font-medium text-sm">{stage.name}</h3>
                    <div className="text-xs text-muted-foreground mt-1">
                      {stage.status === "completed" ? "Completed" : 
                      stage.status === "in-progress" ? `${stage.progress}% Complete` : 
                      "Not Started"}
                    </div>
                    {stage.status === "in-progress" && (
                      <div className="mt-2 h-1 w-full bg-gray-100 rounded-full">
                        <div
                          className="h-1 bg-blue-500 rounded-full"
                          style={{ width: `${stage.progress}%` }}
                        />
                      </div>
                    )}
                    
                    {stage.informationExchanges && stage.informationExchanges.length > 0 && (
                      <div className="flex items-center mt-2 text-xs">
                        <FileCheck className="h-3 w-3 mr-1" />
                        <span>
                          {getInfoExchangeStatus(stage).complete}/{getInfoExchangeStatus(stage).total}
                        </span>
                      </div>
                    )}
                    
                    <ChevronRightIcon className="h-4 w-4 text-muted-foreground mt-2" />
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <div className="p-2 max-w-xs space-y-1">
                  <h4 className="font-semibold">{stage.name} (Stage {stage.id})</h4>
                  <p className="text-xs whitespace-pre-line">{getStageDetails(stage)}</p>
                  <p className="text-xs text-muted-foreground">Click to view details</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
      
      {/* Current stage information */}
      <div className="border rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center">
              Current Stage: {stages[currentStage].name}
              <Badge className="ml-2" variant={stages[currentStage].status === "completed" ? "default" : "secondary"}>
                {stages[currentStage].status === "completed" ? "Completed" : "In Progress"}
              </Badge>
            </h3>
            <p className="text-sm text-muted-foreground">{stages[currentStage].description}</p>
          </div>
          
          <div className="mt-2 md:mt-0 text-right">
            <div className="text-sm font-medium">{stages[currentStage].progress}% Complete</div>
            <div className="w-40 mt-1">
              <Progress value={stages[currentStage].progress} />
            </div>
          </div>
        </div>
        
        {stages[currentStage].informationExchanges && stages[currentStage].informationExchanges.length > 0 && (
          <div className="mt-3">
            <div className="text-sm font-medium mb-2">Required Information Exchange</div>
            <div className="flex flex-wrap gap-2">
              {stages[currentStage].informationExchanges.map((exchange, index) => (
                <Badge 
                  key={exchange.id} 
                  variant={exchange.status === "validated" ? "default" : 
                          exchange.status === "complete" ? "secondary" : 
                          "outline"}
                  className="flex items-center"
                >
                  {exchange.status === "validated" ? (
                    <CheckIcon className="h-3 w-3 mr-1" />
                  ) : exchange.status === "complete" ? (
                    <FileCheck className="h-3 w-3 mr-1" />
                  ) : (
                    <Clock className="h-3 w-3 mr-1" />
                  )}
                  {exchange.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Current stage cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => handleStageClick(currentStage)}>
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <ClipboardList className="h-5 w-5 mr-2 text-primary" />
              <CardTitle className="text-sm font-medium">Activities</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stages[currentStage]?.activities?.length || 0}</div>
            <p className="text-sm text-muted-foreground">Required items in this stage</p>
            <ChevronRightIcon className="h-4 w-4 text-muted-foreground mt-2" />
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => handleStageClick(currentStage)}>
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />
              <CardTitle className="text-sm font-medium">Risks</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {stages[currentStage] ? 
                (stages[currentStage].id === 1 ? 3 : 0) : 0}
            </div>
            <p className="text-sm text-muted-foreground">Active risks in this stage</p>
            <ChevronRightIcon className="h-4 w-4 text-muted-foreground mt-2" />
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => handleStageClick(currentStage)}>
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-secondary" />
              <CardTitle className="text-sm font-medium">Timeline</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">23 days</div>
            <p className="text-sm text-muted-foreground">Remaining in this stage</p>
            <ChevronRightIcon className="h-4 w-4 text-muted-foreground mt-2" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
